import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { useSiteContent } from "../lib/siteContent";
import { imgUrl, imgSrcSet } from "../lib/img";
import { ArrowRight, Check, KeyRound, Star } from "lucide-react";

const Kicker = ({ children, tone = "green" }) => (
  <div className="flex items-center gap-3">
    <span className={`h-px w-8 ${tone === "lime" ? "bg-[#CDE05F]" : "bg-[#0F6B3B]"}`} />
    <span
      className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${
        tone === "lime" ? "text-[#CDE05F]" : "text-[#0F6B3B]"
      }`}
    >
      {children}
    </span>
  </div>
);

export default function LandingPage() {
  const { content } = useSiteContent();
  const l = content.landing || {};
  const h = l.hero || {};
  const steps = l.steps || {};
  const feature = l.feature || {};
  const tms = l.testimonials || {};
  const band = l.code_band || {};
  const bottom = l.cta || {};

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F2]">
      <Header
        marketing
        right={
          <>
            <Link
              to="/auth"
              className="hidden sm:block text-sm font-semibold text-[#1F2A24] hover:text-[#0F6B3B]"
              data-testid="header-auth-link"
            >
              Client code
            </Link>
            <Link to="/subscribe" data-testid="header-subscribe-link">
              <Button className="h-10 rounded-md bg-[#0F6B3B] px-5 sm:px-6 text-sm font-semibold text-white hover:bg-[#0B5730]">
                Subscribe
              </Button>
            </Link>
          </>
        }
      />

      {/* ── HERO ──────────────────────────────────────── */}
      <section className="pt-10 pb-0 sm:pt-16" data-testid="hero-section">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6 rise-in">
            <Kicker>{h.kicker}</Kicker>
            <h1 className="mt-6 font-display text-[2.6rem] sm:text-6xl lg:text-[4.4rem] font-bold leading-[1.02] tracking-[-0.03em] text-[#14211B]">
              {h.title_prefix}
              <span className="block text-[#0F6B3B]">{h.title_highlight}</span>
            </h1>
            <p className="mt-6 max-w-md text-[15px] sm:text-base leading-relaxed text-[#56655C]">
              {h.subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/subscribe" className="w-full sm:w-auto" data-testid="hero-cta-subscribe">
                <Button className="group h-12 w-full sm:w-auto rounded-md bg-[#0F6B3B] px-6 text-sm font-semibold text-white hover:bg-[#0B5730]">
                  {h.primary_cta}
                  <ArrowRight className="ml-2.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="/#plans" className="w-full sm:w-auto" data-testid="hero-cta-how">
                <Button
                  variant="outline"
                  className="h-12 w-full sm:w-auto rounded-md border-[#D6DACB] bg-white px-6 text-sm font-semibold text-[#1F2A24] hover:bg-[#F3F1E7]"
                >
                  {h.secondary_cta}
                </Button>
              </a>
            </div>
            <div className="mt-7 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-display text-base font-semibold text-[#14211B]">{h.price}</span>
              <span className="text-sm text-[#7A8880]">{h.price_note}</span>
            </div>
          </div>

          {/* Hero image, arch-topped, with floating card + lime badge */}
          <div className="lg:col-span-6 relative">
            <div className="relative mx-auto max-w-[520px] lg:max-w-none">
              {h.image_url && (
                <img
                  src={imgUrl(h.image_url, 1000)}
                  srcSet={imgSrcSet(h.image_url, [520, 820, 1200])}
                  sizes="(max-width: 1024px) 100vw, 46vw"
                  alt="A prepared meal bowl"
                  className="arch-top w-full aspect-[4/5] sm:aspect-[5/5] object-cover"
                />
              )}
              <div
                className="absolute -top-3 -right-1 sm:-right-4 h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-[#CDE05F] grid place-items-center rotate-[-8deg] p-3 text-center"
                data-testid="hero-badge"
              >
                <span className="font-display text-[9px] sm:text-[10px] font-semibold uppercase leading-tight tracking-[0.04em] text-[#14211B]">
                  {h.badge}
                </span>
              </div>
              <div
                className="absolute bottom-6 -left-1 sm:left-[-28px] max-w-[230px] bg-white p-4 sm:p-5 shadow-[0_18px_40px_-24px_rgba(20,33,27,0.4)]"
                data-testid="hero-card"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0F6B3B]">
                  {h.card_label}
                </div>
                <div className="font-display mt-1.5 text-lg font-semibold leading-snug text-[#14211B]">
                  {h.card_title}
                </div>
                <div className="mt-2 text-xs text-[#7A8880]">{h.card_note}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFIT BAR ───────────────────────────────── */}
      <section className="mt-14 sm:mt-20 bg-[#0F6B3B]" data-testid="benefits-bar">
        <div className="max-w-[1320px] mx-auto grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/15">
          {(l.benefits || []).map((b, i) => (
            <div
              key={i}
              className="flex items-center justify-center gap-2.5 px-6 py-4 text-center"
              data-testid={`benefit-${i}`}
            >
              <Check className="h-4 w-4 flex-none text-[#CDE05F]" />
              <span className="text-sm text-white">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOUR STEPS ────────────────────────────────── */}
      <section id="plans" className="py-16 sm:py-24" data-testid="steps-section">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col items-center text-center">
            <Kicker>{steps.kicker}</Kicker>
            <h2 className="mt-5 max-w-2xl font-display text-[2rem] sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-[-0.03em] text-[#14211B]">
              {steps.title}
            </h2>
            <p className="mt-4 max-w-xl text-[15px] text-[#56655C]">{steps.subtitle}</p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(steps.items || []).map((s, i) => (
              <div
                key={i}
                className="border-t-2 border-t-[#0F6B3B] border border-[#E7E3D7] bg-white p-6 transition-transform duration-300 hover:-translate-y-1"
                data-testid={`step-${i}`}
              >
                <div className="h-9 w-9 rounded-full bg-[#E4EBD8] grid place-items-center font-display text-xs font-semibold text-[#0F6B3B]">
                  {s.n}
                </div>
                <div className="font-display mt-6 text-lg font-semibold text-[#14211B]">{s.t}</div>
                <p className="mt-2.5 text-sm leading-relaxed text-[#6A776F]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE BAND ──────────────────────────────── */}
      <section id="meals" className="bg-[#E4EBD8] py-16 sm:py-24" data-testid="feature-section">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6 relative">
            {feature.image_url && (
              <img
                src={imgUrl(feature.image_url, 900)}
                srcSet={imgSrcSet(feature.image_url, [520, 820, 1200])}
                sizes="(max-width: 1024px) 100vw, 46vw"
                loading="lazy"
                alt={feature.title_prefix}
                className="w-full aspect-[4/3] object-cover"
              />
            )}
            {feature.image_tag && (
              <div
                className="absolute bottom-4 right-0 sm:right-[-18px] bg-white px-4 py-3 text-xs font-semibold text-[#14211B] shadow-[0_14px_34px_-22px_rgba(20,33,27,0.5)]"
                data-testid="feature-image-tag"
              >
                {feature.image_tag}
              </div>
            )}
          </div>

          <div className="lg:col-span-6">
            <Kicker>{feature.kicker}</Kicker>
            <h2 className="mt-5 font-display text-[2rem] sm:text-4xl lg:text-[2.9rem] font-bold leading-[1.08] tracking-[-0.03em] text-[#14211B]">
              {feature.title_prefix}{" "}
              <span className="block sm:inline">{feature.title_highlight}</span>
            </h2>
            <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-[#4F5F56]">
              {feature.body}
            </p>
            <ul className="mt-7 space-y-4">
              {(feature.bullets || []).map((b, i) => (
                <li key={i} className="flex gap-3" data-testid={`feature-bullet-${i}`}>
                  <span className="mt-0.5 h-5 w-5 flex-none rounded-full bg-[#0F6B3B] grid place-items-center">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  <span className="text-sm leading-relaxed text-[#6A776F]">
                    <span className="font-semibold text-[#14211B]">{b.lead}</span> {b.rest}
                  </span>
                </li>
              ))}
            </ul>
            <Link to="/subscribe" className="mt-8 inline-block" data-testid="feature-cta">
              <Button
                variant="outline"
                className="group h-12 rounded-md border-[#0F6B3B]/40 bg-transparent px-6 text-sm font-semibold text-[#0F6B3B] hover:bg-white"
              >
                {feature.cta}
                <ArrowRight className="ml-2.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────── */}
      <section id="about" className="bg-[#FDFCF7] py-16 sm:py-24" data-testid="testimonials-section">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col items-center text-center">
            <Kicker>{tms.kicker}</Kicker>
            <h2 className="mt-5 font-display text-[2rem] sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-[-0.03em] text-[#14211B]">
              {tms.title}
            </h2>
            <p className="mt-4 max-w-xl text-[15px] text-[#56655C]">{tms.subtitle}</p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(tms.items || []).map((t, i) => (
              <div
                key={i}
                className="flex flex-col justify-between border border-[#E7E3D7] bg-white p-6"
                data-testid={`testimonial-${i}`}
              >
                <div>
                  <div className="flex gap-1">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="h-3.5 w-3.5 fill-[#0F6B3B] text-[#0F6B3B]" />
                    ))}
                  </div>
                  <p className="font-quote mt-5 text-[15px] leading-relaxed text-[#3F4D45]">
                    “{t.quote}”
                  </p>
                </div>
                <div className="mt-8 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#14211B]">
                  {t.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLIENT CODE BAND ──────────────────────────── */}
      <section className="py-14 sm:py-16" data-testid="code-band">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="bg-[#E4EBD8] p-6 sm:p-10 grid gap-6 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <Kicker>{band.kicker}</Kicker>
              <h2 className="mt-4 font-display text-[1.6rem] sm:text-3xl font-bold tracking-[-0.02em] text-[#14211B]">
                {band.title}
              </h2>
              <p className="mt-2 text-sm text-[#56655C]">{band.subtitle}</p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end w-full lg:w-auto">
              <Link to="/auth" data-testid="code-band-cta" className="block">
                <Button className="h-12 w-full lg:w-auto rounded-md bg-[#0F6B3B] px-6 text-sm font-semibold text-white hover:bg-[#0B5730]">
                  <KeyRound className="mr-2.5 h-4 w-4" />
                  {band.cta}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLOSING CTA ───────────────────────────────── */}
      <section className="bg-[#0F6B3B] py-16 sm:py-24 text-center" data-testid="cta-section">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col items-center">
          <Kicker tone="lime">{bottom.kicker}</Kicker>
          <h2 className="mt-5 max-w-3xl font-display text-[2rem] sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-[-0.03em] text-white">
            {bottom.title}
          </h2>
          <p className="mt-5 max-w-2xl text-[15px] text-white/75">{bottom.subtitle}</p>
          <div className="mt-9 flex w-full flex-col sm:w-auto sm:flex-row gap-3">
            <Link to="/subscribe" className="w-full sm:w-auto" data-testid="bottom-cta-subscribe">
              <Button className="group h-12 w-full sm:w-auto rounded-md bg-[#CDE05F] px-6 text-sm font-semibold text-[#14211B] hover:bg-white">
                {bottom.primary}
                <ArrowRight className="ml-2.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <Link to="/auth" className="w-full sm:w-auto" data-testid="bottom-cta-code">
              <Button
                variant="outline"
                className="h-12 w-full sm:w-auto rounded-md border-white/60 bg-transparent px-6 text-sm font-semibold text-white hover:bg-white/10 hover:text-white"
              >
                {bottom.secondary}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
